import { useInView } from '../../hooks/useInView';

interface Testimonial {
  name: string;
  role: string;
  rating: number;
  quote: string;
  avatar: string;
}

interface TestimonialsProps {
  eyebrow?: string;
  headline: string;
  testimonials: Testimonial[];
  ctaText?: string;
  ctaHref?: string;
}

export default function SectionTestimonials({
  eyebrow = 'Testimonials',
  headline,
  testimonials,
  ctaText = 'Start Your Journey',
  ctaHref = '#contact',
}: TestimonialsProps) {
  const { ref, inView } = useInView();
  const hero = testimonials[0];
  const rest = testimonials.slice(1);

  return (
    <section id="testimonials" className="bg-[#0A1628] py-28 md:py-40 relative overflow-hidden">
      {/* Decorative accents */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-[#00B4D8]/20 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[700px] h-[500px] bg-[#00B4D8]/4 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px w-14 bg-gradient-to-r from-transparent to-[#00B4D8]" />
            <span className="text-[#00B4D8] text-[10px] font-black uppercase tracking-[0.35em]">{eyebrow}</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="text-4xl md:text-[54px] font-black text-white leading-[1.05] max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {headline}
            </h2>
            <div className="flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-amber-400 text-sm" />
                ))}
              </div>
              <span className="text-white/40 text-sm">5.0 from 200+ reviews</span>
            </div>
          </div>
        </div>

        {/* Hero testimonial */}
        {hero && (
          <div className="mb-6 bg-gradient-to-br from-white/8 to-white/3 border border-white/10 rounded-2xl p-10 md:p-14 relative overflow-hidden hover:border-white/20 transition-all duration-500">
            {/* Large decorative quote */}
            <div
              className="absolute -top-4 -left-2 text-[#00B4D8]/8 select-none pointer-events-none"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: '200px', lineHeight: 1 }}
            >
              &ldquo;
            </div>

            <div className="relative z-10">
              {/* Stars */}
              <div className="flex items-center gap-1 mb-8">
                {Array.from({ length: hero.rating }).map((_, si) => (
                  <i key={si} className="ri-star-fill text-amber-400 text-base" />
                ))}
              </div>

              {/* Quote */}
              <p
                className="text-white text-xl md:text-[26px] leading-relaxed mb-10 max-w-4xl font-light"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                &ldquo;{hero.quote}&rdquo;
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border-2 border-[#00B4D8]/40">
                    <img src={hero.avatar} alt={hero.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <div className="text-white font-bold text-base">{hero.name}</div>
                    <div className="text-[#00B4D8] text-sm mt-0.5">{hero.role}</div>
                  </div>
                </div>
                {/* Divider accent */}
                <div className="hidden sm:flex items-center gap-4">
                  <div className="h-px w-24 bg-white/10" />
                  <span className="text-white/15 text-xs uppercase tracking-widest">Verified Diver</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Supporting testimonials grid */}
        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
            {rest.map((t, i) => (
              <div
                key={t.name}
                className="bg-white/4 border border-white/8 rounded-2xl p-8 flex flex-col hover:bg-white/8 hover:-translate-y-1 hover:border-white/15 transition-all duration-300 cursor-default"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: t.rating }).map((_, si) => (
                      <i key={si} className="ri-star-fill text-amber-400 text-xs" />
                    ))}
                  </div>
                  <div
                    className="text-[#00B4D8]/15 font-serif leading-none text-5xl select-none"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    &ldquo;
                  </div>
                </div>

                {/* Quote */}
                <p className="text-white/65 text-[15px] leading-relaxed flex-1 mb-7 italic">
                  &ldquo;{t.quote}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-5 border-t border-white/8">
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-[#00B4D8]/25">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm">{t.name}</div>
                    <div className="text-[#00B4D8] text-[11px] mt-0.5">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-white/8">
          <p className="text-white/35 text-sm max-w-md">
            Thousands of Miami divers have trained and dived with Ace Diving. Your experience starts with one conversation.
          </p>
          <a
            href={ctaHref}
            className="inline-flex items-center gap-2.5 bg-[#00B4D8] hover:bg-[#00C9F0] text-white font-bold px-10 rounded-full transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer text-[15px] shrink-0"
            style={{ paddingTop: '18px', paddingBottom: '18px' }}
          >
            {ctaText}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
